import React, { useEffect, useState, useContext } from "react";
import { RoleContext } from "../../contexts/RoleContext";
import { AuthContext } from "../../contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import Notification from "../../components/UI/Notification";

const EditRole = () => {
  const { id: roleId } = useParams(); // Extract roleId from route params
  const navigate = useNavigate(); // To redirect after update
  const { token } = useContext(AuthContext);
  const { currentRole, fetchRoleDetails, editRole, error, clearError } =
    useContext(RoleContext);

  const [newRoleName, setNewRoleName] = useState("");
  const [message, setMessage] = useState("");
  const [hasFetched, setHasFetched] = useState(false); // Track if details are fetched
  const [notification, setNotification] = useState(null);

  // Fetch role details only once for the current roleId
  useEffect(() => {
    const fetchDetails = async () => {
      // console.log(hasFetched)
      // console.log(token)
      // console.log(roleId)
      if (!hasFetched && token && roleId) {
        try {
          // console.log(`Fetching details for roleId: ${roleId}`);
          clearError();
          await fetchRoleDetails(token, roleId);
          setHasFetched(true); // Mark as fetched to prevent re-fetching
          // console.log(`Details fetched for roleId: ${roleId}`);
        } catch (err) {
          // console.error("Error fetching role details:", err);
          // setNotification("Error fetching role details:", err);
          setNotification({
            message: `Error fetching role details:, ${err}`,
            type: "error",
          });
        }
      }
    };

    fetchDetails();
  }, [token, roleId, hasFetched, fetchRoleDetails, clearError]);

  // Update local state when currentRole changes
  useEffect(() => {
    if (currentRole) {
      setNewRoleName(currentRole.name || "");
    }
  }, [currentRole]);

  // Handle role update
  const handleUpdateRole = async () => {
    setMessage("");
    clearError();
    if (!newRoleName.trim()) {

      setNotification({
        message: "Role name cannot be empty.",
        type: "error",
      });
      return;
    }
    // console.log(newRoleName)
    try {
      await editRole(token, roleId, { name: newRoleName });

      setNotification({
        message: "Role updated successfully.",
        type: "success",
      });
      navigate("/admin/roles"); // Redirect to roles list
    } catch (err) {
      // setMessage("Failed to update the role.");
      console.error("Failed to update role:", err);
      // setNotification("Failed to update role:", err);
      setNotification({
        message: `Failed to update role:, ${err}`,
        type: "error",
      });
    }
  };

  // Display loading, error, or not-found states
  if (!hasFetched && !currentRole) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Loading role details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!currentRole) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Role not found.</p>
      </div>
    );
  }

  return (
    <div className=" flex items-center justify-center p-6">
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}
      <div className="bg-white dark:bg-gray-900 w-full max-w-md p-6 rounded shadow-md">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center dark:text-white">Edit Role</h2>

        {/* Success or error message */}
        {message && (
          <p
            className={`text-sm font-medium mb-4 ${
              message.includes("successfully") ? "text-green-600" : "text-red-600"
            }`}
          >
            {message}
          </p>
        )}

        {/* Role details */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-2 dark:text-white">Role ID: <span className='text-red-500'>*</span></label>
          <p className="bg-gray-100 px-4 py-2 dark:border dark:bg-black dark:text-white dark:border-gray-700 rounded-lg">{currentRole.id}</p>
        </div>

        {/* Edit role name */}
        <div className="mb-6">
          <label className="block text-gray-700 font-medium mb-2 dark:text-white">Role Name: <span className='text-red-500'>*</span></label>
          <input
            type="text"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            className="w-full px-4 py-2 dark:bg-black dark:text-white dark:border-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter new role name"
          />
        </div>

        {/* Update button */}
        <button
          onClick={handleUpdateRole}
          className={`w-full py-2 rounded-lg transition duration-200 ${
            newRoleName.trim()
              ? "bg-blue-600 text-white hover:bg-blue-700"
              : "bg-gray-400 text-gray-300 cursor-not-allowed"
          }`}
          disabled={!newRoleName.trim()}
        >
          Update Role
        </button>
      </div>
    </div>
  );
};

export default EditRole;
